import { ExpandMore } from '@mui/icons-material';
import { DatePicker } from '@mui/lab';
import {
  Accordion,
  AccordionDetails,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  Radio,
  RadioGroup,
  Typography,
  useEventCallback,
} from '@mui/material';
import { FiltersFormFamily } from 'family/view-models/family-list';
import { FormikErrors, FormikProps, FormikTouched } from 'formik';
import { useNavigate } from 'react-router-dom';
import { PmiButton } from 'shell/components/shared/button';
import MunicipalityList, { MunicipalityAnprVM } from 'shell/components/shared/municipality-list';
import FiltersIdentityCard from 'shell/components/shared/person-search/filters/identity-card';
import FiltersResidencePermit from 'shell/components/shared/person-search/filters/residence-permit';
import FilterSearchByFiscalCode from 'shell/components/shared/person-search/filters/search-by-fiscal-code';
import FiltersSubjectData from 'shell/components/shared/person-search/filters/subject-data';
import { StickyDiv } from 'shell/components/shared/sticky-div';
import { AccordionSummary } from 'shell/components/shared/summary-accordion';
import PmiTextField from 'shell/components/shared/text-field';
import { FiltersPersonSearch } from 'shell/constants/filters-person-search';
import { ISTAT_MILANO } from 'shell/constants/istat-milano';
import { useUserProfile } from 'shell/hooks/use-user-profile';
import {
  IdentityCardVM,
  ResidencePermitVM,
  SearchByFiscalCodeVM,
  SubjectDataVM,
} from 'shell/view-models/search-anpr';

interface FiltersPersonProps {
  filterFamilyForm: FormikProps<FiltersFormFamily>;
  gobackUrl?: string;
}

const FilterFamily = ({
  filterFamilyForm,
  gobackUrl = '/',
}: FiltersPersonProps): React.ReactElement => {
  const { userProfile } = useUserProfile();
  const navigate = useNavigate();

  const onChangeMunicipalityList = useEventCallback((municipality: MunicipalityAnprVM | null) => {
    filterFamilyForm.setFieldValue('municipalityOfResidence', municipality);
  });

  const onOpenAccordion = useEventCallback((value: FiltersPersonSearch) => {
    filterFamilyForm.setValues({
      isMunicipality: false,
      codeAnpr: '',
      codeMunicipality: '',
      codeIstat: '',
      page: filterFamilyForm.values.page,
      rowsPerPage: filterFamilyForm.values.rowsPerPage,
      orders: filterFamilyForm.values.orders,
      searchByFiscalCode: { fiscalCode: '' },
      subjectData: {},
      identityCard: {},
      residencePermit: {},
      municipalityOfResidence: filterFamilyForm.values.municipalityOfResidence,
      isNationalResearch: filterFamilyForm.values.isNationalResearch,
      openedAccordion: value,
    });
  });

  const handleChangeSearchByFiscalCode = useEventCallback(
    (key: keyof SearchByFiscalCodeVM, value: SearchByFiscalCodeVM[keyof SearchByFiscalCodeVM]) => {
      filterFamilyForm.setFieldValue('searchByFiscalCode', {
        ...(filterFamilyForm.values?.searchByFiscalCode || {}),
        [key]: value,
      });
    }
  );

  const handleChangeFieldsSearchByFiscalCode = useEventCallback((values: SearchByFiscalCodeVM) => {
    filterFamilyForm.setFieldValue('searchByFiscalCode', {
      ...(filterFamilyForm.values?.searchByFiscalCode || {}),
      ...values,
    });
  });

  const handleChangeSubjectData = useEventCallback(
    (key: keyof SubjectDataVM, value: SubjectDataVM[keyof SubjectDataVM]) => {
      filterFamilyForm.setFieldValue('subjectData', {
        ...(filterFamilyForm.values?.subjectData || {}),
        [key]: value,
      });
    }
  );

  const handleChangeFieldsSubjectData = useEventCallback((values: SubjectDataVM) => {
    filterFamilyForm.setFieldValue('subjectData', {
      ...(filterFamilyForm.values?.subjectData || {}),
      ...values,
    });
  });

  const handleChangeIdentityCard = useEventCallback(
    (key: keyof IdentityCardVM, value: IdentityCardVM[keyof IdentityCardVM]) => {
      filterFamilyForm.setFieldValue('identityCard', {
        ...(filterFamilyForm.values?.identityCard || {}),
        [key]: value,
      });
    }
  );

  const handleChangeResidencePermit = useEventCallback(
    (key: keyof ResidencePermitVM, value: ResidencePermitVM[keyof ResidencePermitVM]) => {
      filterFamilyForm.setFieldValue('residencePermit', {
        ...(filterFamilyForm.values?.residencePermit || {}),
        [key]: value,
      });
    }
  );

  return (
    <form onSubmit={filterFamilyForm.handleSubmit} onReset={filterFamilyForm.handleReset}>
      <Grid container spacing={2}>
        <Grid item xs={12} md={3}>
          <StickyDiv>
            <div className="pmi-pb-4 pmi-d-block">
              <PmiButton variant="contained" color="cdmBrandThird" type="submit" fullWidth>
                Cerca
              </PmiButton>
            </div>
            <div className="pmi-pb-4 pmi-d-block">
              <PmiButton variant="outlined" color="cdmBrandThird" type="reset" fullWidth>
                Pulisci
              </PmiButton>
            </div>
            <div className="pmi-d-block">
              <PmiButton
                variant="outlined"
                color="cdmBrand"
                fullWidth
                onClick={() => navigate(gobackUrl)}
              >
                Indietro
              </PmiButton>
            </div>
          </StickyDiv>
        </Grid>
        <Grid item xs={12} md={9}>
          {/* Fields outside accordion */}
          <Grid container spacing={2} alignItems="center" marginBottom={2}>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    id="isNationalResearch"
                    checked={filterFamilyForm.values.isNationalResearch || false}
                    onChange={() => {
                      filterFamilyForm.setValues({
                        ...filterFamilyForm.values,
                        isNationalResearch: !filterFamilyForm.values.isNationalResearch,
                        municipalityOfResidence: !filterFamilyForm.values.isNationalResearch
                          ? null
                          : {
                              id: userProfile.datiLogin?.codiceIstat,
                              description: userProfile.datiLogin?.nomeComune || '',
                              province: userProfile.datiLogin?.siglaProvincia || '',
                            },
                      });
                    }}
                    inputProps={{ 'aria-label': 'Ricerca nazionale' }}
                  />
                }
                label="Ricerca nazionale"
              />
            </Grid>
            <Grid item xs={12} md={5}>
              <MunicipalityList
                value={filterFamilyForm.values.municipalityOfResidence || null}
                onChange={onChangeMunicipalityList}
                disabled={filterFamilyForm.values.isNationalResearch}
                label="Comune di residenza"
                id="municipalityOfResidence"
              />
            </Grid>
            <Grid item xs={12} md={5}>
              <PmiTextField
                aria-label="Provincia"
                label="Provincia"
                id="provinceOfResidence"
                name="provinceOfResidence"
                fullWidth
                type="text"
                disabled
                variant="outlined"
                value={filterFamilyForm.values.municipalityOfResidence?.province || ''}
              />
            </Grid>
            <Grid item xs={12}>
              <Divider />
            </Grid>
          </Grid>

          {/* Accordion: Ricerca per Famiglia */}
          <Accordion
            expanded={filterFamilyForm.values.openedAccordion === FiltersPersonSearch.Family}
            onChange={() => {
              onOpenAccordion(FiltersPersonSearch.Family);
            }}
          >
            <AccordionSummary
              aria-controls="panel-family-filters-content"
              id="panel-family-filters-header"
              classes={{
                root: 'pmi-flex-row-reverse',
              }}
              expandIcon={<ExpandMore />}
            >
              <Typography>Ricerca per Famiglia</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <FormControl component="fieldset">
                    <FormLabel component="legend">Identificativo</FormLabel>
                    <RadioGroup
                      row
                      aria-label="Identificativo"
                      id="isMunicipality"
                      value={filterFamilyForm.values.isMunicipality || 'false'}
                      onChange={() => {
                        filterFamilyForm.setValues({
                          ...filterFamilyForm.values,
                          codeMunicipality: '',
                          codeIstat: '',
                          codeAnpr: '',
                          isMunicipality: !filterFamilyForm.values.isMunicipality,
                        });
                      }}
                    >
                      <FormControlLabel value="false" control={<Radio />} label="Comune" />
                      <FormControlLabel value="true" control={<Radio />} label="ANPR" />
                    </RadioGroup>
                  </FormControl>
                </Grid>
                {!filterFamilyForm.values.isMunicipality ? (
                  <>
                    <Grid item xs={12} md={5}>
                      <PmiTextField
                        label="Identificativo del Comune"
                        aria-label="Identificativo del Comune"
                        fullWidth
                        inputProps={{
                          maxLength: '20',
                        }}
                        onChange={(event) => {
                          if (filterFamilyForm.values.isNationalResearch) {
                            filterFamilyForm.setFieldValue('codeMunicipality', event?.target.value);
                            return;
                          }
                          filterFamilyForm.setValues({
                            ...filterFamilyForm.values,
                            codeMunicipality: event?.target.value,
                            codeIstat: event?.target.value ? ISTAT_MILANO : '',
                          });
                        }}
                        id="code-municipality"
                        value={filterFamilyForm.values.codeMunicipality || ''}
                        helperText={filterFamilyForm.errors.codeMunicipality}
                        error={
                          filterFamilyForm.touched.codeMunicipality &&
                          Boolean(filterFamilyForm.errors.codeMunicipality)
                        }
                      />
                    </Grid>
                    <Grid item xs={12} md={5}>
                      <PmiTextField
                        label="Cod. ISTAT Comune"
                        aria-label="Codice ISTAT Comune"
                        fullWidth
                        id="code-istat-municipality"
                        inputProps={{
                          maxLength: '6',
                        }}
                        onChange={(event) => {
                          filterFamilyForm.setFieldValue('codeIstat', event?.target.value);
                        }}
                        value={filterFamilyForm.values.codeIstat || ''}
                        helperText={filterFamilyForm.errors.codeIstat}
                        error={
                          filterFamilyForm.touched.codeIstat &&
                          Boolean(filterFamilyForm.errors.codeIstat)
                        }
                      />
                    </Grid>
                  </>
                ) : (
                  <Grid item xs={12} md={5}>
                    <PmiTextField
                      label="Identificativo ANPR"
                      aria-label="Identificativo ANPR"
                      id="identifier-anpr"
                      fullWidth
                      inputProps={{
                        maxLength: '15',
                      }}
                      onChange={(event) => {
                        filterFamilyForm.setFieldValue('codeAnpr', event?.target.value);
                      }}
                      value={filterFamilyForm.values.codeAnpr || ''}
                      helperText={filterFamilyForm.errors.codeAnpr}
                      error={
                        filterFamilyForm.touched.codeAnpr &&
                        Boolean(filterFamilyForm.errors.codeAnpr)
                      }
                    />
                  </Grid>
                )}
                <Grid item container spacing={2} xs={12}>
                  <Grid item xs={12} md={5}>
                    <DatePicker
                      label="Data di riferimento scheda"
                      aria-label="Data di riferimento scheda"
                      value={filterFamilyForm.values.dateForm || null}
                      onChange={(date) => {
                        filterFamilyForm.setFieldValue('dateForm', date);
                      }}
                      renderInput={(params) => (
                        <PmiTextField id="dateForm" {...params} fullWidth helperText="" />
                      )}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>

          {/* Accordion: Ricerca per codice fiscale */}
          <FilterSearchByFiscalCode
            open={filterFamilyForm.values.openedAccordion === FiltersPersonSearch.FiscalCode}
            onOpenAccordion={onOpenAccordion}
            values={filterFamilyForm.values.searchByFiscalCode}
            onChange={handleChangeSearchByFiscalCode}
            touched={
              filterFamilyForm.touched
                .searchByFiscalCode as unknown as FormikTouched<SearchByFiscalCodeVM>
            }
            errors={
              filterFamilyForm.errors.searchByFiscalCode as FormikErrors<SearchByFiscalCodeVM>
            }
            onChangeValues={handleChangeFieldsSearchByFiscalCode}
          />

          {/* Accordion: Ricerca per dati del soggetto */}
          <FiltersSubjectData
            open={filterFamilyForm.values.openedAccordion === FiltersPersonSearch.SubjectData}
            onOpenAccordion={onOpenAccordion}
            values={filterFamilyForm.values.subjectData}
            onChange={handleChangeSubjectData}
            touched={
              filterFamilyForm.touched.subjectData as unknown as FormikTouched<SubjectDataVM>
            }
            errors={filterFamilyForm.errors.subjectData as FormikErrors<SubjectDataVM>}
            onChangeValues={handleChangeFieldsSubjectData}
          />

          {/* Accordion: Ricerca per Carta d'identità */}
          <FiltersIdentityCard
            open={filterFamilyForm.values.openedAccordion === FiltersPersonSearch.IdentityCard}
            onOpenAccordion={onOpenAccordion}
            values={filterFamilyForm.values.identityCard}
            onChange={handleChangeIdentityCard}
            touched={
              filterFamilyForm.touched.identityCard as unknown as FormikTouched<IdentityCardVM>
            }
            errors={filterFamilyForm.errors.identityCard as FormikErrors<IdentityCardVM>}
          />

          {/* Accordion: Ricerca per Permesso di soggiorno */}
          <FiltersResidencePermit
            open={filterFamilyForm.values.openedAccordion === FiltersPersonSearch.ResidencePermit}
            onOpenAccordion={onOpenAccordion}
            values={filterFamilyForm.values.residencePermit}
            onChange={handleChangeResidencePermit}
            touched={
              filterFamilyForm.touched
                .residencePermit as unknown as FormikTouched<ResidencePermitVM>
            }
            errors={filterFamilyForm.errors.residencePermit as FormikErrors<ResidencePermitVM>}
          />
        </Grid>
      </Grid>
    </form>
  );
};

FilterFamily.displayName = 'Filters Family component';

export default FilterFamily;
